import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { generatePath, useNavigate, useParams } from 'react-router-dom'

import { Typography } from 'antd'
import moment from 'moment'

import { icons, SmallSpinner } from 'portal/lib/primitives'
import * as routes from 'portal/routes'
import { datePickerChanged } from 'portal/store/global/actions'
import { loadRecordsCount } from 'portal/store/home/actions'
import { uiSetShownWasteTableColumns } from 'portal/store/ui/actions'
import { useMixpanel } from 'portal/utils/mixpanel'

import {
  Tile,
  TileNumber,
  TileRow,
  Tiles,
  TitleBar,
  TitleBarIcon
} from './commonStyles'

export const Waste = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { accountId, projectId } = useParams<{
    accountId: string
    projectId: string
  }>()
  const { track } = useMixpanel()

  const { Title } = Typography

  const userUuid = useSelector((state: any) => state.auth.userUuid)

  useEffect(() => {
    if (accountId && projectId) {
      dispatch(loadRecordsCount(accountId, projectId, 'waste', 'total', {}))
      dispatch(loadRecordsCount(accountId, projectId, 'waste', 'today', {}))
      dispatch(
        loadRecordsCount(accountId, projectId, 'waste', 'last30Days', {})
      )
    }
  }, [dispatch, accountId, projectId])

  useEffect(() => {
    if (accountId && projectId && userUuid) {
      dispatch(
        loadRecordsCount(accountId, projectId, 'waste', 'assignedToMe', {
          assignedTo: userUuid
        })
      )
    }
  }, [dispatch, accountId, projectId, userUuid])

  const wasteCounts = useSelector((state: any) => state.home.waste)

  const updateGlobalDateRangeAndGoToRecords = (period: string) => {
    let startDate: string | null = null
    const endDate = moment().endOf('day').toISOString()

    if (period === 'today') {
      startDate = moment().startOf('day').toISOString()
    } else if (period === 'last30Days') {
      startDate = moment().subtract(30, 'days').startOf('day').toISOString()
    } else {
      startDate = '2018-01-01T00:00:00.000Z'
    }

    // Ensure 'removalDate' is in waste-shownTableColumns in local storage and Redux State and that 'createdAt' is not.
    const shownColumnsString =
      localStorage.getItem('waste-shownTableColumns') || '[]'
    let shownColumns = JSON.parse(shownColumnsString)

    if (!shownColumns.includes('removalDate')) {
      shownColumns.unshift('removalDate')
    }

    if (shownColumns.includes('createdAt')) {
      shownColumns = shownColumns.filter((col: string) => col !== 'createdAt')
    }

    dispatch(uiSetShownWasteTableColumns(shownColumns))
    localStorage.setItem(
      'waste-shownTableColumns',
      JSON.stringify(shownColumns)
    )

    // Reset waste filters and if assignedToMe update in session storage, else clear all filters
    const wasteFilters: { [key: string]: any } = {}

    if (period === 'assignedToMe' && userUuid) {
      wasteFilters.assignedTo = [userUuid]
    }
    sessionStorage.setItem('waste-filters', JSON.stringify(wasteFilters))

    dispatch(
      datePickerChanged(
        {
          startDate,
          endDate
        },
        'global'
      )
    )

    if (accountId && projectId) {
      // Track the click on the tile with Mixpanel and navigate to the waste records page with the date range applied
      track(`frontend_home_page_tile_clicked`, {
        project_id: projectId,
        account_id: accountId,
        section: 'waste',
        tile: period
      })

      setTimeout(() => {
        navigate(
          generatePath(routes.WASTE_TABLE, {
            accountId: accountId,
            projectId: projectId
          })
        )
      }, 100)
    }
  }

  return (
    <>
      <TitleBar>
        <TitleBarIcon style={{ marginTop: 4 }}>
          <icons.Modules.Waste />
        </TitleBarIcon>
        <Title level={4}>Waste Records</Title>
      </TitleBar>
      <Tiles>
        <Tile onClick={() => updateGlobalDateRangeAndGoToRecords('total')}>
          <TileRow>
            <Title level={5}>Total</Title>
          </TileRow>
          <TileRow>
            {wasteCounts.total.loading ? (
              <SmallSpinner />
            ) : (
              <TileNumber>
                {new Intl.NumberFormat().format(wasteCounts?.total.count)}
              </TileNumber>
            )}
          </TileRow>
        </Tile>

        <Tile onClick={() => updateGlobalDateRangeAndGoToRecords('today')}>
          <TileRow>
            <Title level={5}>Today</Title>
          </TileRow>
          <TileRow>
            {wasteCounts.today.loading ? (
              <SmallSpinner />
            ) : (
              <TileNumber>
                {new Intl.NumberFormat().format(wasteCounts?.today.count)}
              </TileNumber>
            )}
          </TileRow>
        </Tile>

        <Tile onClick={() => updateGlobalDateRangeAndGoToRecords('last30Days')}>
          <TileRow>
            <Title level={5}>Last 30 days</Title>
          </TileRow>
          <TileRow>
            {wasteCounts.last30Days.loading ? (
              <SmallSpinner />
            ) : (
              <TileNumber>
                {new Intl.NumberFormat().format(wasteCounts?.last30Days.count)}
              </TileNumber>
            )}
          </TileRow>
        </Tile>

        <Tile
          onClick={() => updateGlobalDateRangeAndGoToRecords('assignedToMe')}
        >
          <TileRow>
            <Title level={5}>Assigned to you</Title>
          </TileRow>
          <TileRow>
            {wasteCounts.assignedToMe.loading ? (
              <SmallSpinner />
            ) : (
              <TileNumber>
                {new Intl.NumberFormat().format(
                  wasteCounts?.assignedToMe.count
                )}
              </TileNumber>
            )}
          </TileRow>
        </Tile>
      </Tiles>
    </>
  )
}
